import { Component, ErrorInfo, ReactNode } from 'react';
import { isChunkLoadError, shouldAutoReloadForChunkError } from '@/hooks/useVersionCheck';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('React Error Boundary caught error:', error.message, {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      componentStack: errorInfo.componentStack,
    });

    // Auto-reload once for chunk load errors (stale JS after deploy)
    if (isChunkLoadError(error) && shouldAutoReloadForChunkError()) {
      window.location.reload();
      return;
    }

    this.setState({ errorInfo });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-dark-200 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-dark-100 rounded-lg border border-destructive/30 overflow-hidden">
            {/* Header */}
            <div className="bg-destructive/10 px-6 py-4 border-b border-destructive/30">
              <h1 className="text-xl font-bold text-destructive flex items-center gap-2">
                <span>⚠</span> Something went wrong
              </h1>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="text-gray-300">
                An unexpected error occurred. The error has been logged for debugging.
              </p>

              {/* Error Details */}
              {this.state.error && (
                <div className="bg-dark-300 rounded-lg p-4 font-mono text-sm">
                  <div className="text-destructive font-bold mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <pre className="text-gray-500 text-xs overflow-x-auto whitespace-pre-wrap">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={this.handleReset}
                  className="btn-primary px-4 py-2"
                >
                  Try Again
                </button>
                <a href="/" className="btn-secondary px-4 py-2">
                  Go Home
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
